import { useState, useEffect, useCallback, useRef } from 'react';

interface BackendHealthState {
  isHealthy: boolean;
  isLoading: boolean;
  error: string | null;
  lastChecked: Date | null;
}

const HEALTH_CHECK_INTERVAL = 60000; // 1 minute (reduced frequency)
const MAX_RETRY_ATTEMPTS = 2; // Reduced retry attempts
const RETRY_DELAY = 5000; // 5 seconds (increased delay)
const HEALTH_CHECK_TIMEOUT = 3000; // 3 seconds timeout

export const useBackendHealth = () => {
  const [health, setHealth] = useState<BackendHealthState>({
    isHealthy: true,
    isLoading: false,
    error: null,
    lastChecked: null,
  });

  const intervalRef = useRef<number | null>(null);
  const retryCountRef = useRef(0);
  const lastCheckRef = useRef<number>(0);
  const debounceTimeoutRef = useRef<number | null>(null);

  const checkHealth = useCallback(async (isRetry = false, immediate = false) => {
    const now = Date.now();
    const timeSinceLastCheck = now - lastCheckRef.current;

    // Debounce: Don't check if we've checked within the last 10 seconds (unless immediate)
    if (!immediate && !isRetry && timeSinceLastCheck < 10000) {
      return; // Skip the check to avoid rate limiting
    }

    // Clear any existing debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    const performHealthCheck = async () => {
      lastCheckRef.current = Date.now();

      if (!isRetry) {
        setHealth(prev => ({ ...prev, isLoading: true, error: null }));
      }

      try {
        // Use a simple health check endpoint with timeout
        const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/health`, {
          method: 'GET',
          signal: AbortSignal.timeout(HEALTH_CHECK_TIMEOUT), // 3 second timeout
        });

        if (response.ok) {
          setHealth({
            isHealthy: true,
            isLoading: false,
            error: null,
            lastChecked: new Date(),
          });
          retryCountRef.current = 0;
        } else {
          throw new Error(`Server responded with ${response.status}`);
        }
      } catch (error) {
        console.error('Backend health check failed:', error);

        let errorMessage = 'Backend service tidak tersedia';

        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            errorMessage = 'Backend service terlalu lama merespon';
          } else if (error.message.includes('ECONNREFUSED')) {
            errorMessage = 'Backend service tidak dapat dihubungi';
          } else if (error.message.includes('NetworkError')) {
            errorMessage = 'Terjadi kesalahan jaringan';
          }
        }

        setHealth({
          isHealthy: false,
          isLoading: false,
          error: errorMessage,
          lastChecked: new Date(),
        });

        // Retry logic for automatic recovery
        if (!isRetry && retryCountRef.current < MAX_RETRY_ATTEMPTS) {
          retryCountRef.current++;
          setTimeout(() => {
            checkHealth(true, false);
          }, RETRY_DELAY * retryCountRef.current);
        }
      }
    };

    // If immediate, perform check right away
    if (immediate) {
      performHealthCheck();
    } else {
      // Otherwise, debounce with a short delay
      debounceTimeoutRef.current = setTimeout(performHealthCheck, 500);
    }
  }, []);

  const startHealthCheck = useCallback(() => {
    // Initial check
    checkHealth();

    // Set up periodic checks
    intervalRef.current = setInterval(() => {
      checkHealth();
    }, HEALTH_CHECK_INTERVAL);
  }, [checkHealth]);

  const stopHealthCheck = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, []);

  const forceCheck = useCallback(() => {
    retryCountRef.current = 0;
    checkHealth(false, true); // Force immediate check
  }, [checkHealth]);

  useEffect(() => {
    startHealthCheck();

    return () => {
      stopHealthCheck();
    };
  }, [startHealthCheck, stopHealthCheck]);

  // Set up visibility change handler to pause/resume checks when tab is not visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopHealthCheck();
      } else {
        startHealthCheck();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [startHealthCheck, stopHealthCheck]);

  // Listen for backend error events from API service
  useEffect(() => {
    const handleBackendError = (event: CustomEvent) => {
      console.log('Backend error event received:', event.detail);
      // Force an immediate health check when backend errors occur
      checkHealth();
    };

    window.addEventListener('backend:error', handleBackendError as EventListener);

    return () => {
      window.removeEventListener('backend:error', handleBackendError as EventListener);
    };
  }, [checkHealth]);

  return {
    ...health,
    forceCheck,
    startHealthCheck,
    stopHealthCheck,
  };
};